import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { SearchResponse } from '../../models/search-response';
import {
  AssigneeConnectionDetails,
  Company,
  ContainerSasUrl,
} from '../../../../shared/interfaces/company';
import { RecordDetailsComponent } from '../../../../shared/components/record-details/record-details.component';
import { SourceTypes } from '../../models/search-source-types';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { LoggedInUser } from '../../../../shared/models/logged-in-user';
import { AuthService } from '../../../auth/services/auth.service';
import { DatePipe } from '@angular/common';
import { RecordVisibilityMode } from '../../../../shared/enums/record-visibility';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { ImageService } from '../../../../shared/services/image-service/image.service';
import { take, takeUntil } from 'rxjs';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { LeadsService } from '../../../leads/services/leads.service';
import { AssignLeadResponse } from '../../../leads/models/assign-lead-body';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { formatAddress } from '../../../../shared/utils/utils';

@Component({
  selector: 'app-search-result-v2',
  templateUrl: './search-result-v2.component.html',
  styleUrls: ['./search-result-v2.component.scss'],
  standalone: true,
  imports: [RecordDetailsComponent, SvgIconComponent, DatePipe],
})
export class SearchResultV2Component
  extends DestroyBase
  implements OnInit, AfterViewInit
{
  @Input() searchResultsList!: SearchResponse;
  @Input() selectedTab!: string;
  @Input() selectedSubTab!: string;
  @Input() isSearchPage = false;
  @Input() containerSasUrl!: ContainerSasUrl;
  @Input() totalPages = 0;
  @Input() currentPage = 0;
  @Input() recordId: string | null = null;

  @Output() companyDeleted = new EventEmitter<string>();
  @Output() companyDetailsUpdated = new EventEmitter<Company>();
  @Output() assignedLead = new EventEmitter<Company | null>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() showInMap = new EventEmitter<Company[]>();
  @Output() companyViewed = new EventEmitter<string | null>();
  @Output() triggerSearchResults = new EventEmitter<boolean>();

  private authService = inject(AuthService);
  private imageService = inject(ImageService);
  cdr = inject(ChangeDetectorRef);
  private leadsService = inject(LeadsService);
  private commonService = inject(CommonService);
  private ngZone = inject(NgZone);

  recordVisibilityModes = RecordVisibilityMode;

  allCheckboxChecked = false;
  selectedCompanies: Company[] = [];

  hasUnassigned = false;

  company!: Company;
  isRecordDetailsPopupOpen = false;

  loggedInUserInfo: LoggedInUser | null = null;

  cdfiLogo: string | null = null;

  hideAssignToMe = !this.authService.hasPermission(
    PermissionCode.GET_SET_LEADS,
  );

  assignedItemId = 0;

  ngOnInit() {
    this.loggedInUserInfo = this.authService.getLoggedInUserInfo();
    this.imageService.connectionImageUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((image) => {
        this.cdfiLogo = image;
        this.cdr.markForCheck();
      });
    if (this.recordId !== null) {
      const foundCompany = this.searchResultsList.results.find(
        (com) => com.unique_id === this.recordId,
      );

      if (foundCompany) {
        this.company = foundCompany;
        this.isRecordDetailsPopupOpen = true;
      }
    }
  }

  ngAfterViewInit(): void {
    this.findAssignedItem();
  }

  onAllCheckBoxChange(event: Event, companies: Company[]): void {
    this.allCheckboxChecked = (event.target as HTMLInputElement).checked;
    if ((event.target as HTMLInputElement).checked) {
      this.selectedCompanies = companies.filter(
        (company) => !this.isVlfRowDisabled(company),
      );
    } else if (!(event.target as HTMLInputElement).checked) {
      this.selectedCompanies = [];
    }
    this.hasUnassignedSelectedCompanies();
  }

  onSelectCompany(company: Company, event: Event): void {
    if (this.isVlfRowDisabled(company)) return;

    if ((event.target as HTMLInputElement).checked) {
      this.selectedCompanies.push(company);
    } else if (!(event.target as HTMLInputElement).checked) {
      this.selectedCompanies = this.selectedCompanies.filter(
        (item) => item !== company,
      );
    }
    this.hasUnassignedSelectedCompanies();
  }

  onShowInMap(): void {
    if (this.selectedCompanies.length > 0) {
      this.showInMap.emit(this.selectedCompanies);
    }
  }

  getFormatAddress(address: string): string {
    return formatAddress(address);
  }

  viewDetail(company: Company) {
    if (this.isVlfRowDisabled(company)) return;

    this.company = company;
    this.isRecordDetailsPopupOpen = true;
    this.companyViewed.emit(company.unique_id);
  }

  closeRecordDetailsPopup() {
    this.isRecordDetailsPopupOpen = false;
    this.recordId = null;
    this.companyViewed.emit(null);
  }

  refreshSearchResults(value: boolean) {
    this.triggerSearchResults.emit(value);
  }

  getSourceName(sourceValue: string): string {
    const source = SourceTypes.find((s) => s.apiValue.includes(sourceValue));
    return source ? source.label : sourceValue;
  }

  getAssigneeBadges(company: Company): {
    initial: string;
    name: string;
    isCurrentUser: boolean;
    assignedDate: Date | undefined;
    cdfi_logo_path: string | null;
  }[] {
    const assignees = company.assignees || [];
    const maxVisible = 3; // Show max 3 badges

    // Map all assignees to badge objects
    const allBadges = assignees.map((assignee) => {
      const connectionName =
        assignee.display_name || assignee.connection_name || '';
      const initial = connectionName.charAt(0).toUpperCase();
      const isCurrentUser =
        this.loggedInUserInfo?.connection === assignee.connection_name ||
        this.loggedInUserInfo?.connection_display_name === connectionName;
      let logo_url: string | null = null;
      if (isCurrentUser) {
        logo_url = this.cdfiLogo;
      } else {
        if (assignee.cdfi_logo_path && this.containerSasUrl) {
          logo_url =
            assignee.cdfi_logo_path + `?${this.containerSasUrl.sas_token}`;
        }
      }
      return {
        initial,
        name: connectionName,
        isCurrentUser,
        assignedDate: assignee.assigned_on_utc,
        cdfi_logo_path: logo_url,
      };
    });

    // Sort badges to put current user first
    const sortedBadges = allBadges.sort((a, b) => {
      if (a.isCurrentUser && !b.isCurrentUser) return -1;
      if (!a.isCurrentUser && b.isCurrentUser) return 1;
      return 0;
    });

    return sortedBadges.slice(0, maxVisible);
  }

  getExtraAssigneesCount(company: Company): number {
    const assignees = company.assignees || [];
    const maxVisible = 3;
    return Math.max(0, assignees.length - maxVisible);
  }

  getExtraAssignees(company: Company): AssigneeConnectionDetails[] {
    const assignees = company.assignees || [];
    const maxVisible = 3;

    // Sort assignees to put current user first (same logic as getAssigneeBadges)
    const sortedAssignees = [...assignees].sort((a, b) => {
      const aIsCurrentUser =
        this.loggedInUserInfo?.connection === a.connection_name ||
        this.loggedInUserInfo?.connection_display_name ===
          (a.display_name || a.connection_name);
      const bIsCurrentUser =
        this.loggedInUserInfo?.connection === b.connection_name ||
        this.loggedInUserInfo?.connection_display_name ===
          (b.display_name || b.connection_name);

      if (aIsCurrentUser && !bIsCurrentUser) return -1;
      if (!aIsCurrentUser && bIsCurrentUser) return 1;
      return 0;
    });

    return sortedAssignees.slice(maxVisible);
  }

  onCompanyDeleted(uniqueId: string): void {
    this.isRecordDetailsPopupOpen = false;
    this.recordId = null;
    this.companyDeleted.emit(uniqueId);
  }

  onCompanyDetailsUpdated(updatedCompany: Company): void {
    if (updatedCompany && this.searchResultsList?.results?.length > 0) {
      const index = this.searchResultsList.results.findIndex(
        (c) => c.unique_id === updatedCompany.unique_id,
      );
      if (index !== -1) {
        this.searchResultsList.results[index] = {
          ...this.searchResultsList.results[index],
          ...updatedCompany,
        };
        this.searchResultsList = {
          ...this.searchResultsList,
          results: [...this.searchResultsList.results],
        };
        this.cdr.detectChanges();
      }
    }
    this.companyDetailsUpdated.emit(updatedCompany);
  }

  OnLeadAssign(updatedCompany: Company | null) {
    if (updatedCompany && this.searchResultsList?.results?.length > 0) {
      const index = this.searchResultsList.results.findIndex(
        (c) => c.unique_id === updatedCompany.unique_id,
      );
      if (index !== -1) {
        this.searchResultsList.results[index] = {
          ...this.searchResultsList.results[index],
          ...updatedCompany,
          assigned_to: updatedCompany.assigned_to,
          assignees: updatedCompany.assignees,
          leadId: updatedCompany.leadId,
        };
        this.searchResultsList = {
          ...this.searchResultsList,
          results: [...this.searchResultsList.results],
        };
        this.cdr.detectChanges();
      }
    }
    this.assignedLead.emit(updatedCompany);
  }

  hasUnassignedSelectedCompanies(): void {
    this.hasUnassigned =
      this.selectedCompanies.length > 0 &&
      !this.hideAssignToMe &&
      this.selectedCompanies.every((company) => {
        // Check if assigned to SYSTEM
        const isAssignedToSystem = company.assigned_to?.group_name === 'SYSTEM';

        // Button should be enabled only if company is not assigned to current user
        // and not assigned to SYSTEM
        return !company.assigned_to && !isAssignedToSystem;
      });
  }

  onAssignToMe(): void {
    if (this.selectedCompanies.length === 0) return;

    const selectedIds = this.selectedCompanies.map(
      (company) => company.unique_id,
    );

    this.leadsService
      .assignLead(this.selectedCompanies)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: AssignLeadResponse) => {
          const successCount = res.success?.length || 0;
          const failureCount = res.failure?.length || 0;

          const failureMessagesGrouped: Record<string, number> = {};

          (
            res.failure as {
              uniqueId: string;
              errorMessage: string;
            }[]
          ).forEach((fail) => {
            const msg = fail.errorMessage;

            failureMessagesGrouped[msg] =
              (failureMessagesGrouped[msg] || 0) + 1;
          });

          if (successCount > 0) {
            this.commonService.triggerToast({
              type: ToastType.Success,
              message: `${successCount} compan${
                successCount === 1 ? 'y' : 'ies'
              } successfully assigned.`,
            });
          }

          if (failureCount > 0) {
            Object.entries(failureMessagesGrouped).forEach(([msg, count]) => {
              this.commonService.triggerToast({
                type: ToastType.Error,
                message: `${count} compan${
                  count === 1 ? 'y' : 'ies'
                } failed: ${msg}`,
              });
            });
          }

          const successIds = res.success.map((item) => item.uniqueId);

          const filteredResults = this.searchResultsList.results.map(
            (company) => {
              const isUpdated =
                selectedIds.includes(company.unique_id) &&
                successIds.includes(company.unique_id);

              if (!isUpdated) {
                return company;
              }

              const currentUserAssignee: AssigneeConnectionDetails = {
                connection_name: this.loggedInUserInfo?.connection ?? '',
                id: 0,
                display_name:
                  this.loggedInUserInfo?.connection_display_name ??
                  this.loggedInUserInfo?.name ??
                  '',
                assigned_on_utc: new Date(),
              };

              const existingAssignees = company.assignees || [];

              const isAlreadyAssigned = existingAssignees.some(
                (assignee) =>
                  assignee.connection_name ===
                  currentUserAssignee.connection_name,
              );

              const updatedAssignees = isAlreadyAssigned
                ? existingAssignees
                : [...existingAssignees, currentUserAssignee];

              return {
                ...company,
                assigned_to: {
                  user_id: undefined,
                  username: this.loggedInUserInfo?.username ?? '',
                  first_name: this.loggedInUserInfo?.name?.split(' ')[0] ?? '',
                  last_name: undefined,
                  group_name: this.loggedInUserInfo?.user_group ?? '',
                },
                assignees: updatedAssignees,
              };
            },
          );

          this.searchResultsList = {
            ...this.searchResultsList,
            results: filteredResults,
          };

          this.allCheckboxChecked = false;
          this.selectedCompanies = [];
          this.hasUnassigned = false;

          this.cdr.detectChanges();
        },

        error: () => {
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: 'Failed to assign companies.',
          });

          this.selectedCompanies = [];
          this.hasUnassigned = false;
          this.allCheckboxChecked = false;
        },
      });
  }

  onPageChange(value: number) {
    this.pageChange.emit(value);
  }

  findAssignedItem(): void {
    this.assignedItemId = 0;
    if (this.searchResultsList?.results?.length === 0) {
      return;
    }

    for (let i = 0; i < this.searchResultsList.results.length; i++) {
      const company = this.searchResultsList.results[i];
      if (company.assignees && company.assignees.length > 0) {
        this.assignedItemId = i;
        break;
      }
    }

    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      this.waitForRecordElementExistence();
    });
  }

  waitForRecordElementExistence(assignedItemId = 0, retry = 0): void {
    const assignedElement = document.getElementById(
      `tour-record-item-${this.assignedItemId}`,
    );

    if (assignedElement) {
      assignedElement.id = 'tour-record-assigned-item';
    }

    const assignedToElement = document.getElementById(
      `tour-record-assigned-to-${this.assignedItemId}`,
    );

    if (assignedToElement) {
      assignedToElement.id = 'tour-record-assigned-item-assigned-to';
    }

    const recordViewElement = document.getElementById(
      `tour-record-item-view-${this.assignedItemId}`,
    );

    if (recordViewElement) {
      recordViewElement.id = 'tour-record-item-view';
    }

    const checkboxElement = document.getElementById(
      `tour-record-item-checkbox-${this.assignedItemId}`,
    );

    if (checkboxElement) {
      checkboxElement.id = 'tour-record-item-checkbox';
      return;
    }

    if (retry < 10) {
      setTimeout(
        () => this.waitForRecordElementExistence(retry + 1, assignedItemId),
        100,
      );
    }
  }
  getVlfExpiresIn(company: Company): { label: string; urgent: boolean } {
    let businessDays: number;
    let fromDate: Date;

    const assignedRecord =
      company.assignees?.find((a) => a.assigned_on_utc) ||
      company.assignees?.[0];
    if (assignedRecord?.assigned_on_utc) {
      // Assigned — 7 business days from when the org claimed it
      businessDays = 7;
      fromDate = new Date(assignedRecord.assigned_on_utc);
    } else {
      // Unclaimed — 3 business days from reallocation if any, else record creation
      businessDays = 3;
      fromDate = new Date(
        company.vlf_reallocated_on_utc ?? company.created_on_utc,
      );
    }

    const expiry = new Date(fromDate);
    let daysAdded = 0;

    while (daysAdded < businessDays) {
      expiry.setDate(expiry.getDate() + 1);

      const day = expiry.getDay();

      // Monday = 1 ... Friday = 5
      if (day >= 1 && day <= 5) {
        daysAdded++;
      }
    }

    const msLeft = expiry.getTime() - Date.now();

    if (msLeft <= 0) {
      return { label: 'Expired', urgent: true };
    }

    const hoursLeft = Math.floor(msLeft / (1000 * 60 * 60));
    const daysLeft = Math.floor(hoursLeft / 24);
    const remainingHours = hoursLeft % 24;

    const urgent = daysLeft < 2;

    const label =
      daysLeft > 0 ? `${daysLeft}d ${remainingHours}h` : `${hoursLeft}h`;

    return {
      label: `Expires in ${label}`,
      urgent,
    };
  }

  isVlfClaimedByOtherOrg(company: Company): boolean {
    if (company.source !== 'VLF' || !company.assignees?.length) return false;
    const myConnection = this.loggedInUserInfo?.connection;
    return !company.assignees.some((a) => a.connection_name === myConnection);
  }

  isVlfExpired(company: Company): boolean {
    return (
      company.source === 'VLF' &&
      !this.isVlfFunded(company) &&
      this.getVlfExpiresIn(company).label === 'Expired'
    );
  }

  isVlfRowDisabled(company: Company): boolean {
    return this.isVlfClaimedByOtherOrg(company) || this.isVlfExpired(company);
  }

  isVlfFunded(company: Company): boolean {
    if (company.source !== 'VLF') return false;
    const status = (company.lead_status || '').toLowerCase();
    return status.includes('funded');
  }
}
